// BrushCursorOverlay — キャンバス上の独自ブラシカーソル (presentation のみ).
//
// なぜ必要か (consult 2026-05-24):
//   ペンタブで描画開始時に DrawingCanvas が `canvas.setPointerCapture(e.pointerId)`
//   を呼ぶ (= ストローク中にペン先が canvas 外へ出ても pointermove を受け続ける
//   ために必須). Chromium 系は **pen を pointer capture すると capture 中だけ
//   ネイティブ OS カーソルを隠す** ため、描画中にカーソルが消える. これを独自
//   カーソルで補い、かつブラシ位置・サイズの可視化で UX も向上させる.
//
// 設計上の責務分離 (architecture.md §16 / §19.6):
//   - drawing engine (CanvasDrawingEngine / worker / 2層レンダリング): 一切触らない.
//     このオーバーレイは pixel を描かない. engine の zoom 値 (= transform: scale)
//     と現在のブラシ幅を「読むだけ」.
//   - frontend (このファイル): canvas と同じ relative wrapper の中に
//     `position: absolute; pointer-events: none` の要素を散布し、
//     wrapper の pointermove で位置を、props (brushWidth / zoom / cursorType /
//     strokeActive) で表示切替・サイズを追従する.
//
// 表示仕様 (2026-07-25 方式 B′ — 矢印モードのストローク中消失対策、consult 確定):
//   - カーソル種別「ブラシ円」(既定、`cursorType==='brush-ring'`):
//     従来どおり **hover 中も描画中も常にブラシ径のリング (円の輪郭) のみ**を表示。
//     canvas 側は styles.css が `cursor: none` を当てて OS カーソルを隠す。
//     2026-06-21 (consult、α フィードバック 123 さん): 中心ドットは廃止済み
//     (findability は `MIN_BRUSH_CURSOR_DIAMETER_PX` の最小径 clamp が担う)。
//   - カーソル種別「矢印」(`cursorType==='arrow'`, Java 版の懐古フック):
//     以前は canvas を `cursor: auto` にして本 component を **render しなかった**
//     が、Chromium が pen capture 中ネイティブカーソルを隠すため描画中に矢印が
//     消える不具合があった (2026-05-24 コメント参照)。方式 B′ = 「hover もスト
//     ローク中も同一の独自矢印グリフ」にしてすり替わりの違和感を消す:
//       - hover: canvas の CSS `cursor` (= `arrowCursorGlyph.ts` の SVG を
//         `url(...)  hotspot, auto` で指定、styles.css 側) がネイティブ描画で
//         矢印を出す (ゼロ遅延)。
//       - ストローク中 (pointerdown→pointerup、`strokeActive` prop): 本
//         component が **同じ SVG** を `<img>` overlay として描き、canvas 側は
//         `data-oekaki-stroking` 属性で `cursor: none` に切り替わる (二重矢印
//         防止、CanvasStage 側の配線)。
//     矢印グリフは **固定サイズ** (リングと違い zoom / ブラシ幅でスケールしない)
//     で、ホットスポット (= 矢印の先端) をポインタ位置に合わせる (リングの
//     中央寄せとは異なるアンカー方式)。
//   - サイズ (リングのみ): 画面 px 上のリング直径 = `brushWidth × zoom`. canvas は
//     engine が `transform: scale(zoom)` (transform-origin: 0 0) で拡大するので、
//     ブラシ幅・zoom が変われば直径も追従する.
//
// 座標系:
//   - リスナは wrapper (canvas の親 relative div) に張る. canvas は描画中
//     `setPointerCapture` 状態でも pointermove を受け続け、それが wrapper に
//     bubble するので「hover 時も描画中も」同じ経路で位置更新できる.
//   - 位置は `e.clientX/Y` を `CanvasViewMapper.clientToLocal` (`viewport.ts` §1b) で
//     wrapper-local px に変換して `transform: translate(...)` で当てる (旧:
//     wrapper の getBoundingClientRect との差分。回転すると wrapper の rect が外接
//     矩形に膨らむため mapper へ集約). wrapper-local px は
//     既に zoom 適用後の視覚座標 (wrapper の box が native*zoom) なので、別途
//     zoom を掛ける必要はない (= NameLabelOverlay とは違い、ここは clientXY 基準).
//   - レビュー指摘 (2026-07-25): 位置更新は pointermove **と** pointerdown の
//     両方で行う. pointermove の無いタッチ (hover 無し、pointerdown→pointerup
//     だけの点打ち) では、pointerdown で位置を確定させないと矢印グリフが
//     「一度も transform されない初期値 (=左上)」または「前回ストロークの
//     古い位置」のまま opacity だけ 1 になり、誤った位置に表示されてしまう
//     (strokeActive の opacity effect は位置を触らないため). pointerdown は
//     必ず strokeActive が true になる前に発火するので、同一ディスパッチ内で
//     位置を先に確定できる.
//
// 性能 (高頻度 pointermove 対策):
//   - React setState で毎 move 再レンダリングしない. ref + 直接 DOM style 更新で
//     位置を反映する (= DrawingCanvas の raf flush が ref を使う流儀に合わせる).
//   - サイズ (brushWidth / zoom) は低頻度変更なので effect で style に流す.
//   - `strokeActive` / `cursorType` はストローク開始/終了・設定変更でしか変わらない
//     低頻度値なので、専用 effect で opacity を切り替える (再レンダリングは
//     CanvasStage 側で isStroking state が起きるが、頻度は stroke 単位で軽微).
//
// イベントを奪わない:
//   - root も子要素もすべて `pointer-events: none`. 既存の pointer 処理
//     (pen-only モード / palm rejection / spuit / imagepaste preview / ペン尻
//     消しゴム) を一切壊さない.

import type { CustomCursor } from '@oekaki/protocol';
import { useEffect, useMemo, useRef } from 'react';

import type { BrushCursorType } from '../../../infrastructure/brushCursorPreference.js';
import {
  type CanvasViewMapper,
  COUNTER_ROTATE_TRANSFORM,
} from '../../../infrastructure/drawing/viewport.js';
import { CustomCursorCanvas } from '../shared/CustomCursorCanvas.js';
import { CUSTOM_CURSOR_HOTSPOT_OFFSET_PX } from '../shared/customCursorDraw.js';
import { decodeCursorPixels } from '../shared/customCursorPixels.js';
import {
  ARROW_CURSOR_DATA_URI,
  ARROW_CURSOR_HOTSPOT,
  ARROW_CURSOR_SIZE_PX,
} from './arrowCursorGlyph.js';

/**
 * 自分のカスタムカーソル (custom-cursor-2026-09-19.md §2.5-1) のサムネ表示
 * サイズ (CSS px)。ペン先の右下に固定オフセットで追従する
 * (ring と違い brushWidth/zoom に非依存 — 矢印グリフと同じ「固定サイズ」方針)。
 *
 * オフセット自体は `CUSTOM_CURSOR_HOTSPOT_OFFSET_PX` (shared/customCursorDraw.ts)
 * を使う (作者 staging フィードバック 2026-09-19 follow-up「表示位置を自分と相手で
 * 統一」— 旧固定値 10px の自前定義をやめ、`NameLabelOverlay` の peer カーソルと
 * 同じ 1 つの定数を参照する).
 */
export const OWN_CURSOR_THUMB_SIZE_PX = 24;

export interface BrushCursorOverlayProps {
  /**
   * 画面 ⇔ canvas 座標の変換窓口。ポインタの client 座標を wrapper-local px へ変換する
   * (参照は mount 中不変、値は呼び出しごとに最新を読む)。
   */
  readonly viewMapper: CanvasViewMapper;
  /** 現在のブラシ幅 (= canvas native px). 画面 px 直径は brushWidth × zoom. */
  readonly brushWidth: number;
  /** 現在の表示 zoom (1..16). engine.getZoom() / onZoomChange と同値. */
  readonly zoom: number;
  /**
   * カーソル種別 (共有 chrome = useRoomChrome.brushCursorType と同値).
   * 'brush-ring' = 常時リング表示 (従来仕様)。'arrow' = 矢印グリフを
   * `strokeActive` の間だけ表示 (方式 B′、2026-07-25)。
   */
  readonly cursorType: BrushCursorType;
  /**
   * ストローク進行中か (pointerdown〜pointerup/cancel の間 true)。'arrow' の
   * ときだけ参照する (= 矢印グリフの表示条件)。CanvasStage が DrawingCanvas の
   * `onStrokeActiveChange` シグナルをそのまま渡す (= プレビュー抑制と同じ
   * 発火源、pointer capture の実タイミングと一致する).
   */
  readonly strokeActive: boolean;
  /**
   * 自分の確定済みカスタムカーソル (custom-cursor-2026-09-19.md §2.5-1)。
   * `null`/`undefined` なら何も描かない (= 未設定、または feature 無効の
   * 古い server 応答)。`RoomState.members[]` から自分の uid で引いた値を渡す。
   */
  readonly cursor?: CustomCursor | null;
  /**
   * 自分のカーソルを自分の画面にも表示するか (作者 staging フィードバック
   * 2026-09-19 follow-up、既定 true)。false のときはこの overlay の自分サムネ
   * だけを止める — `cursor` の配信・相手側の表示・参加者リストのサムネイルには
   * 一切影響しない (それらは別 component/経路)。
   */
  readonly showOwnCursor?: boolean;
}

/**
 * ブラシ幅 (canvas native px) と zoom から、画面 px 上のリング直径を求める純関数.
 *
 * canvas は engine が `transform: scale(zoom)` で拡大するので、native px の
 * ブラシ幅は画面上では `brushWidth × zoom` px に見える. テスト容易性のため
 * component から分離する (NameLabelOverlay の computeNameLabelPosition と同流儀).
 *
 * - 非有限値 / 負値の brushWidth・zoom は防御的に 0 / 1 へ倒す (= NaN が DOM の
 *   width/height に流れて描画が壊れるのを防ぐ).
 * - **見つけやすい最小径** を確保する. 細ブラシ (例 2px) では実径のリングが小さ
 *   すぎて「カーソルが見えない」体感が残るため、実径ではなく下限
 *   `MIN_BRUSH_CURSOR_DIAMETER_PX` まで膨らませる. 太ブラシは実径のまま (= 下限を
 *   上回るので clamp されない).
 *
 *   2026-06-21 (consult、α フィードバック 123 さん): 中心ドット (旧 3px) を廃止した
 *   ため、この最小径 clamp が **唯一の小径 findability 担保** になった (= ドットの
 *   代替)。旧 8px は中心ドットと併存する前提の下限だったので、ドット無しで「リング
 *   だけで位置が分かる」最小サイズへ 10px に引き上げる (= リングの輪郭が背景の明暗
 *   どちらでも視認でき、かつ中心の描画位置をリング内側の中央として読み取れる径)。
 *   太ブラシ・拡大時はこの下限を上回るので影響しない (= 実径のリングがそのまま出る)。
 */
export const MIN_BRUSH_CURSOR_DIAMETER_PX = 10;

export const computeBrushCursorScreenDiameter = (brushWidth: number, zoom: number): number => {
  const w = Number.isFinite(brushWidth) && brushWidth > 0 ? brushWidth : 0;
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  return Math.max(MIN_BRUSH_CURSOR_DIAMETER_PX, w * z);
};

export const BrushCursorOverlay = ({
  viewMapper,
  brushWidth,
  zoom,
  cursorType,
  strokeActive,
  cursor,
  showOwnCursor = true,
}: BrushCursorOverlayProps): React.ReactNode => {
  // overlay root (absolute inset-0). 親 wrapper にリスナを張る anchor も兼ねる.
  const rootRef = useRef<HTMLDivElement | null>(null);
  // ブラシ円モードのリング要素. 位置は transform で、サイズは width/height で更新.
  const ringRef = useRef<HTMLDivElement | null>(null);
  // 矢印モードの矢印グリフ要素 (方式 B′). サイズは固定 (zoom/brushWidth 非依存)。
  const arrowRef = useRef<HTMLImageElement | null>(null);
  // 自分のカスタムカーソルのサムネ (custom-cursor-2026-09-19.md §2.5-1)。
  // ring/arrow と同じく transform で追従させる (setState しない、高頻度 move 対策).
  const cursorThumbRef = useRef<HTMLDivElement | null>(null);
  const cursorPixels = useMemo(
    () =>
      cursor !== null && cursor !== undefined && showOwnCursor ? decodeCursorPixels(cursor) : null,
    [cursor, showOwnCursor],
  );

  // 高頻度 pointermove ハンドラ (mount 時 1 回だけ登録) から現在の cursorType を
  // 読むための ref. cursorType を effect の依存に含めると move のたびに
  // listener を張り直すことになるため、DrawingCanvas の
  // `onStrokeActiveChangeRef.current = onStrokeActiveChange` と同じ流儀で
  // render のたびに ref を更新するだけに留める.
  const cursorTypeRef = useRef<BrushCursorType>(cursorType);
  cursorTypeRef.current = cursorType;
  // 同上 (mount 時 1 回登録の listener から最新の mapper を読む)。
  const viewMapperRef = useRef<CanvasViewMapper>(viewMapper);
  viewMapperRef.current = viewMapper;

  // サイズ追従 (低頻度): brushWidth / zoom が変わったらリング直径を更新する.
  // 矢印は固定サイズなので対象外 (= <img> の width/height 固定値のまま).
  useEffect(() => {
    const ring = ringRef.current;
    if (ring === null) return;
    const diameter = computeBrushCursorScreenDiameter(brushWidth, zoom);
    ring.style.width = `${diameter}px`;
    ring.style.height = `${diameter}px`;
  }, [brushWidth, zoom]);

  // 矢印グリフの表示切替 (低頻度): 'arrow' モードかつストローク中のときだけ表示.
  // hover では出さない (= ネイティブ矢印カーソルが hover を担当、方式 B′)。
  useEffect(() => {
    const arrow = arrowRef.current;
    if (arrow === null) return;
    arrow.style.opacity = cursorType === 'arrow' && strokeActive ? '1' : '0';
  }, [cursorType, strokeActive]);

  // 位置追従 + リングの hover 表示/非表示 (高頻度): 親 wrapper に pointermove /
  // pointerleave / pointerout を張り、ref で DOM を直接更新する (setState 不使用)。
  useEffect(() => {
    const root = rootRef.current;
    const ring = ringRef.current;
    const arrow = arrowRef.current;
    if (root === null || ring === null || arrow === null) return;
    // 親 wrapper (= canvas / NameLabelOverlay と同じ relative box). canvas が
    // pointer capture 中でも move はここまで bubble する.
    const wrapper = root.parentElement;
    if (wrapper === null) return;

    // 最後のポインタ位置 (client px)。ポインタが止まったままズーム・パン・回転したときに
    // 位置を計算し直すために持つ (staging 実機 fb 2026-10-03「ズームでカーソルが動く」)。
    let lastClient: { x: number; y: number } | null = null;
    const updatePosition = (clientX: number, clientY: number): void => {
      lastClient = { x: clientX, y: clientY };
      const { x, y } = viewMapperRef.current.clientToLocal(clientX, clientY);
      // ring は中央寄せ (`translate(-50%, -50%)`) しているので、left/top に
      // pointer 位置をそのまま当てればリング中心がポインタに一致する.
      ring.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      // 矢印は中央寄せせず、グリフのホットスポット (= 先端) をポインタ位置に
      // 合わせる (リングとアンカー方式が異なる、方式 B′)。
      // 表示回転 (canvas-rotation 段取り 2): wrapper ごと回るので、ホットスポット (= 先端)
      // を軸に逆回転して矢印を画面に対して正立させる (リングは円なのでそのまま)。
      arrow.style.transform = `translate(${x - ARROW_CURSOR_HOTSPOT.x}px, ${
        y - ARROW_CURSOR_HOTSPOT.y
      }px) ${COUNTER_ROTATE_TRANSFORM}`;
      // 自分のカスタムカーソルサムネ (custom-cursor-2026-09-19.md §2.5-1):
      // ring/矢印どちらのモードでも「ペン先の右下」固定オフセットに追従させる
      // (brushWidth/zoom に非依存 — 矢印グリフと同じ「固定サイズ・固定アンカー」方針)。
      // 作者 staging フィードバック 2026-09-19 follow-up: 相手側 (`NameLabelOverlay`
      // の `PeerCursorVisual`) と同じ「整数 px + 同一オフセット定数」にして表示位置を
      // 統一する (= 滲み対策も揃える、`image-rendering: pixelated` は端数 px で
      // ぼやける).
      const thumb = cursorThumbRef.current;
      if (thumb !== null) {
        const thumbLeft = Math.round(x + CUSTOM_CURSOR_HOTSPOT_OFFSET_PX);
        const thumbTop = Math.round(y + CUSTOM_CURSOR_HOTSPOT_OFFSET_PX);
        // サムネはペン先の右下固定オフセット: ペン先 (= 左上から -OFFSET の点) を軸に逆回転。
        thumb.style.transform = `translate(${thumbLeft}px, ${thumbTop}px) ${COUNTER_ROTATE_TRANSFORM}`;
      }
    };

    const onPointerMove = (e: PointerEvent): void => {
      updatePosition(e.clientX, e.clientY);
      // リングの hover 表示は 'brush-ring' モードのみ (= 合意済の常時表示)。
      // 'arrow' モードではリングを一切出さない (矢印グリフの表示は strokeActive
      // 側の effect が制御する、ここでは触らない)。
      if (cursorTypeRef.current === 'brush-ring') {
        ring.style.opacity = '1';
      }
      // カーソルサムネは cursorType に関わらずポインタ追従中は常に表示する
      // (ring/矢印どちらのモードでも「自分だと分かる」演出は独立して出す)。
      const thumb = cursorThumbRef.current;
      if (thumb !== null) thumb.style.opacity = '1';
    };
    // canvas 外に出たら隠してネイティブカーソルを戻す. pointerleave は capture
    // 中は発火しない (= 描画中はカーソルが消えない、意図通り).
    const onPointerLeave = (): void => {
      ring.style.opacity = '0';
      const thumb = cursorThumbRef.current;
      if (thumb !== null) thumb.style.opacity = '0';
    };
    // レビュー指摘 (2026-07-25、f7ad153 後): hover イベントが発生しないタッチで
    // pointermove なしに pointerdown→pointerup だけの「タップ (点打ち)」をすると、
    // 矢印グリフの transform が一度も設定されない (初回) か、前回ストロークの
    // 古い位置に残ったまま (2 回目以降) opacity だけ 1 になり、誤った位置に矢印が
    // 一瞬表示される不具合があった。strokeActive が立って矢印の opacity effect が
    // 走るより前に、同一の pointerdown イベントディスパッチ内で位置を確定させる
    // (= リング側の hover 表示ロジックは触らない、位置更新のみ共有).
    const onPointerDown = (e: PointerEvent): void => {
      updatePosition(e.clientX, e.clientY);
    };

    // touch は hover が無いので接触中 (= pointermove) のみ出る. 害は無い.
    wrapper.addEventListener('pointermove', onPointerMove);
    wrapper.addEventListener('pointerleave', onPointerLeave);
    wrapper.addEventListener('pointerout', onPointerLeave);
    wrapper.addEventListener('pointerdown', onPointerDown);
    const unsubscribeView = viewMapperRef.current.onViewChange(() => {
      if (lastClient !== null) updatePosition(lastClient.x, lastClient.y);
    });
    return () => {
      unsubscribeView();
      wrapper.removeEventListener('pointermove', onPointerMove);
      wrapper.removeEventListener('pointerleave', onPointerLeave);
      wrapper.removeEventListener('pointerout', onPointerLeave);
      wrapper.removeEventListener('pointerdown', onPointerDown);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      // pointer-events:none で既存の pointer 処理を一切奪わない. 初期は非表示
      // (pointermove が来るまで opacity 0) で、突然画面左上にリングが出るのを防ぐ.
      className="pointer-events-none absolute top-0 left-0 h-full w-full"
      data-oekaki-brush-cursor-overlay
    >
      <div
        ref={ringRef}
        data-oekaki-brush-cursor-ring
        // top-0 left-0 起点 + transform で平行移動. box-border でボーダー込みの
        // 直径管理. ring 本体は枠線のみ (= 中心ドットは 2026-06-21 に廃止、
        // findability は MIN_BRUSH_CURSOR_DIAMETER_PX の最小径 clamp が担う).
        className="absolute top-0 left-0 box-border rounded-full border opacity-0"
        style={{
          // 二重色取り (白 + 黒) で背景の明暗どちらでも視認できるリング.
          // border は内側白、外側 box-shadow で黒の薄縁を足す.
          borderColor: 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.55)',
          // willChange で transform 更新を GPU 合成に乗せる (高頻度 move のため).
          willChange: 'transform',
        }}
      />
      {/* 方式 B′ (2026-07-25): 矢印モードのストローク中グリフ。hover では出さず
          (ネイティブ矢印カーソルが担当)、strokeActive の間だけ表示する。
          arrowCursorGlyph.ts と同じ SVG data URI を使う (= 二重定義防止、
          hover とストローク中で見た目が食い違わない)。固定サイズで zoom /
          brushWidth に非依存 (リングとの明確な違い)。 */}
      <img
        ref={arrowRef}
        data-oekaki-brush-cursor-arrow
        src={ARROW_CURSOR_DATA_URI}
        width={ARROW_CURSOR_SIZE_PX}
        height={ARROW_CURSOR_SIZE_PX}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute top-0 left-0 opacity-0"
        style={{
          willChange: 'transform',
          transformOrigin: `${ARROW_CURSOR_HOTSPOT.x}px ${ARROW_CURSOR_HOTSPOT.y}px`,
        }}
      />
      {/* 自分のカスタムカーソル (custom-cursor-2026-09-19.md §2.5-1): ring/矢印の
          「右下」固定オフセットに追従する 24px サムネ。カーソル未設定 (`cursor` が
          null/undefined) なら `CustomCursorCanvas` 自体が何も render しない。 */}
      <div
        ref={cursorThumbRef}
        data-oekaki-own-cursor-thumb
        className="pointer-events-none absolute top-0 left-0 opacity-0"
        style={{
          willChange: 'transform',
          transformOrigin: `${-CUSTOM_CURSOR_HOTSPOT_OFFSET_PX}px ${-CUSTOM_CURSOR_HOTSPOT_OFFSET_PX}px`,
        }}
      >
        <CustomCursorCanvas pixels={cursorPixels} sizePx={OWN_CURSOR_THUMB_SIZE_PX} />
      </div>
    </div>
  );
};
